import {diApi} from '@di-shared/api/di';
import {CreatedMaintenanceRecordRequest, MaintenanceScheduleResponse} from '@di-shared/api/di/__generated';
import {emitEventLog} from '@di-shared/features/analytics';
import BottomSheetWebViewWrapper from '@di-shared/features/common/BottomSheetWebViewWrapper';
import useApiError from '@di-shared/features/common/useApiError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useSestManagementSetting from '@di-shared/features/diag/sest/useSestManagementSetting';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import PreventForm from '@shared/PreventForm';
import usePendingHandler from '@shared/usePendingHandler';

interface SestManagementBottomSheetProps {
  sestCode: string;
  vin: string;
  replaceDate: Date | null | undefined;
  replaceOdometer: number | null | undefined;
  isServiceCenterInput: boolean | undefined;
  historyId: string | undefined | null;
}

const CustomDistanceThreshold = {
  STEP: 1000,
  MIN: (stdDistance: number) => CustomDistanceThreshold._getBound(stdDistance).min,
  MAX: (stdDistance: number) => CustomDistanceThreshold._getBound(stdDistance).max,
  _getBound(midValue: number) {
    const numOfSteps = Math.floor(midValue / 2 / this.STEP);
    return {
      min: midValue - numOfSteps * this.STEP,
      max: midValue + numOfSteps * this.STEP,
    };
  },
};

const CustomDaysThreshold = {
  STEP: 90,
  MIN: (stdDays: number) => stdDays - 2 * CustomDaysThreshold.STEP,
  MAX: (stdDays: number) => stdDays + 2 * CustomDaysThreshold.STEP,
};

function SestManagementSettingBottomSheet({
  sestCode,
  vin,
  replaceOdometer,
  replaceDate,
  isServiceCenterInput,
  historyId,
}: SestManagementBottomSheetProps) {
  const {
    sestName,
    stdDistance,
    managementCycleType,
    maintenanceType,
    customDistance,
    customDaysLeft,
    selectCycleType,
    changeCustomDistance,
    changeCustomDaysLeft,
    stdDays,
  } = useSestManagementSetting({
    sestCode,
    vin,
  });

  const {t} = useInferableTranslation();
  const puid = environmentStore.getState().puid;
  const {resolveError} = useApiError();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertDistanceSymbol, convertDistanceValue} = useUnitConverter();

  const [submitSestManagementForm] = usePendingHandler(
    async () => {
      await diApi.maintenancesScheduleController.updateSestMaintenanceSchedule1({
        vin,
        sestCode: sestCode as CreatedMaintenanceRecordRequest.sestCode,
        requestBody:
          managementCycleType === MaintenanceScheduleResponse.stdType.CUSTOM
            ? {
                puid,
                customStdDistance: customDistance,
                customStdDays: customDaysLeft,
                maintenanceType,
                stdType: MaintenanceScheduleResponse.stdType.CUSTOM,
              }
            : {
                puid,
                customStdDistance: stdDistance,
                customStdDays: stdDays,
                maintenanceType,
                stdType: MaintenanceScheduleResponse.stdType.OWNER_MANUAL,
              },
      });
      window.bottomSheetClose({isChange: true});
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
      window.bottomSheetClose(false);
    }
  );

  return (
    <BottomSheetWebViewWrapper bottomSheetFallbackHeight={300}>
      <PreventForm onSubmit={submitSestManagementForm}>
        <div className="bottom_sheet_area">
          <div className="bottom_sheet">
            <div className="bottom_sheet_header">
              <h2 className="bottom_sheet_title">{sestName}</h2>
              <button
                type="button"
                className="btn btn_ic"
                aria-label={t('T10017')}
                onClick={() => window.bottomSheetClose(null)}
              >
                <span
                  className="ic ic_close_20px"
                  aria-hidden="true"
                ></span>
              </button>
            </div>
            <div className="bottom_sheet_inner">
              <div className="bottom_sheet_contents">
                <div className="replacement_info_area">
                  <h3 className="replacment_title">{t('T30602')}</h3>
                  {replaceDate && replaceOdometer ? (
                    isServiceCenterInput ? (
                      <button
                        type="button"
                        className="btn_replacement"
                        disabled
                      >
                        <div className="replacement_info">
                          <span className="replacement_info_title">
                            {CldrDatetimeFormatter.format(replaceDate, 'yMMMd')}&nbsp;{t('T30296')}
                            <span className="badge badge_basic_container">{t('T30301')}</span>{' '}
                          </span>
                          <span className="replacement_info_base">
                            {t('T30152')}: {formatNumberWithCommas(convertDistanceValue(replaceOdometer))}&nbsp;
                            {convertDistanceSymbol(replaceOdometer)}
                          </span>
                        </div>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn_replacement"
                        disabled={!historyId}
                        onClick={() => window.bottomSheetClose({historyId})}
                      >
                        <div className="replacement_info">
                          <span className="replacement_info_title">
                            {CldrDatetimeFormatter.format(replaceDate, 'yMMMd')}&nbsp;{t('T30296')}
                            <span className="badge badge_basic_container">{t('T30300')}</span>{' '}
                          </span>
                          <span className="replacement_info_base">
                            {t('T30152')}: {formatNumberWithCommas(convertDistanceValue(replaceOdometer))}&nbsp;
                            {convertDistanceSymbol(replaceOdometer)}
                          </span>
                        </div>
                        <span className="ic ic_arrow_forward_16px" />
                      </button>
                    )
                  ) : (
                    <button
                      type="button"
                      className="btn_replacement"
                      disabled
                    >
                      <div className="replacement_info">
                        <span className="replacement_info_desc">{t('T30603')}</span>
                      </div>
                      <span className="ic ic_arrow_forward_16px"></span>
                    </button>
                  )}
                </div>
                <div className="select_group">
                  <h3 className="replacment_title">{t('T30606')}</h3>
                  <div className="input_box">
                    <input
                      type="radio"
                      name="radio_form"
                      id="cycle_standard"
                      className="input"
                      checked={managementCycleType === MaintenanceScheduleResponse.stdType.OWNER_MANUAL}
                      onChange={() => selectCycleType(MaintenanceScheduleResponse.stdType.OWNER_MANUAL)}
                    />
                    <label
                      htmlFor="cycle_standard"
                      className="label"
                    >
                      {t('T30043')}
                      {maintenanceType === MaintenanceScheduleResponse.maintenanceType.COMBINED ? (
                        <span className="sub_text">
                          {t('T30047_$distance_$days', {
                            distance: formatNumberWithCommas(convertDistanceValue(stdDistance)) + convertDistanceSymbol(stdDistance),
                            days: stdDays,
                          })}
                        </span>
                      ) : maintenanceType === MaintenanceScheduleResponse.maintenanceType.DISTANCE ? (
                        <span className="sub_text">
                          {t('T30044_$distance', {
                            distance: formatNumberWithCommas(convertDistanceValue(stdDistance)) + convertDistanceSymbol(stdDistance),
                          })}
                        </span>
                      ) : (
                        <span className="sub_text">{t('T30048_$days', {days: stdDays})}</span>
                      )}
                    </label>
                  </div>
                  <div className="input_box">
                    <input
                      id="cycle_custom"
                      type="radio"
                      name="radio_form"
                      className="input"
                      value={MaintenanceScheduleResponse.stdType.CUSTOM}
                      checked={managementCycleType === MaintenanceScheduleResponse.stdType.CUSTOM}
                      onChange={() => selectCycleType(MaintenanceScheduleResponse.stdType.CUSTOM)}
                    />
                    <label
                      htmlFor="cycle_custom"
                      className="label"
                    >
                      {t('T30045')}
                      {managementCycleType === MaintenanceScheduleResponse.stdType.OWNER_MANUAL && (
                        <span className="sub_text">{t('T30046')}</span>
                      )}
                      {managementCycleType === MaintenanceScheduleResponse.stdType.CUSTOM &&
                        (maintenanceType === MaintenanceScheduleResponse.maintenanceType.COMBINED ? (
                          <span className="sub_text">
                            {t('T30047_$distance_$days', {
                              distance:
                                formatNumberWithCommas(convertDistanceValue(customDistance)) + convertDistanceSymbol(customDistance),
                              days: customDaysLeft,
                            })}
                          </span>
                        ) : maintenanceType === MaintenanceScheduleResponse.maintenanceType.DISTANCE ? (
                          <span className="sub_text">
                            {t('T30044_$distance', {
                              distance:
                                formatNumberWithCommas(convertDistanceValue(customDistance)) + convertDistanceSymbol(customDistance),
                            })}
                          </span>
                        ) : (
                          <span className="sub_text">{t('T30048_$days', {days: customDaysLeft})}</span>
                        ))}
                    </label>
                  </div>

                  {(maintenanceType === MaintenanceScheduleResponse.maintenanceType.COMBINED ||
                    maintenanceType === MaintenanceScheduleResponse.maintenanceType.DISTANCE) &&
                    managementCycleType === MaintenanceScheduleResponse.stdType.CUSTOM && (
                      <div className="slider slider_gauge">
                        <label
                          htmlFor="engineDistanceSlider"
                          className="label_slider"
                        >
                          {t('T30290')}
                        </label>
                        <div className="slider_box">
                          <div
                            className="slider_bar"
                            style={{
                              width: `${((customDistance - CustomDistanceThreshold.MIN(stdDistance)) / (CustomDistanceThreshold.MAX(stdDistance) - CustomDistanceThreshold.MIN(stdDistance))) * 100}%`,
                            }}
                          ></div>
                          <input
                            className="input_slider"
                            value={customDistance}
                            onChange={(e) => changeCustomDistance(+e.target.value)}
                            type="range"
                            min={CustomDistanceThreshold.MIN(stdDistance)}
                            max={CustomDistanceThreshold.MAX(stdDistance)}
                            step={CustomDistanceThreshold.STEP}
                            disabled={managementCycleType !== MaintenanceScheduleResponse.stdType.CUSTOM}
                          />
                        </div>
                      </div>
                    )}

                  {(maintenanceType === MaintenanceScheduleResponse.maintenanceType.COMBINED ||
                    maintenanceType === MaintenanceScheduleResponse.maintenanceType.DAY) &&
                    managementCycleType === MaintenanceScheduleResponse.stdType.CUSTOM && (
                      <div className="slider slider_gauge">
                        <label
                          htmlFor="enginePeriodSlider"
                          className="label_slider"
                        >
                          {t('T30291')}
                        </label>
                        <div className="slider_box">
                          <div
                            className="slider_bar"
                            style={{
                              width: `${((customDaysLeft - CustomDaysThreshold.MIN(stdDays)) / (CustomDaysThreshold.MAX(stdDays) - CustomDaysThreshold.MIN(stdDays))) * 100}%`,
                            }}
                          ></div>

                          <input
                            className="input_slider"
                            value={customDaysLeft}
                            onChange={(e) => changeCustomDaysLeft(+e.target.value)}
                            type="range"
                            min={CustomDaysThreshold.MIN(stdDays)}
                            max={CustomDaysThreshold.MAX(stdDays)}
                            step={CustomDaysThreshold.STEP}
                            disabled={managementCycleType !== MaintenanceScheduleResponse.stdType.CUSTOM}
                          />
                        </div>
                      </div>
                    )}
                </div>
              </div>
              <div className="bottom_sheet_footer">
                <button
                  type="submit"
                  className="btn btn_large btn_filled"
                  onClick={() =>
                    emitEventLog({
                      event: 'consumables_exchange_settings_update',
                      interaction_type: 'cycle setting updated',
                      click_text: t('T30049'),
                      click_location: 'body',
                    })
                  }
                >
                  {t('T30049')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </PreventForm>
    </BottomSheetWebViewWrapper>
  );
}

export default SestManagementSettingBottomSheet;
